import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Lineicons } from '@lineiconshq/react-lineicons';
import { XmarkStroke } from '@lineiconshq/free-icons';
import React, { useState } from 'react';
import { Dialog, DialogOverlay, DialogPortal, DialogTitle } from '@/components/ui/dialog';
import { BottomActionBarV2, AritoIcon } from '@/components/custom/erp';
import { useDialogStack } from '@/hooks/use-dialog-stack';
import { Label } from '@/components/ui/label';

interface RejectDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (action: 'cancel' | 'reject', reason?: string) => void;
  title?: string;
}

const RejectDialog = ({ open, onClose, onConfirm, title = 'Hủy/từ chối duyệt đơn từ' }: RejectDialogProps) => {
  const { contentZIndex, overlayZIndex } = useDialogStack(open);
  const [selectedAction, setSelectedAction] = useState<'cancel' | 'reject'>('reject');
  const [reason, setReason] = useState('');

  const handleConfirm = () => {
    onConfirm(selectedAction, reason);
    onClose();
  };

  const handleClose = () => {
    setSelectedAction('reject');
    setReason('');
    onClose();
  };

  return (
    <Dialog modal={true} open={open} onOpenChange={handleClose}>
      <DialogPortal>
        <DialogOverlay className='fixed inset-0 bg-black/50' style={{ zIndex: overlayZIndex }} />
        <div className='fixed inset-0 flex items-center justify-center p-4' style={{ zIndex: contentZIndex }}>
          <DialogPrimitive.Content
            onOpenAutoFocus={event => event.preventDefault()}
            onCloseAutoFocus={event => event.preventDefault()}
            onInteractOutside={event => event.preventDefault()}
            onPointerDownOutside={event => event.preventDefault()}
            onFocusOutside={event => event.preventDefault()}
            className='flex max-h-[85vh] w-[760px] flex-col overflow-hidden border border-[#cfd8e3] bg-white shadow-lg outline-none'
          >
            <div className='flex h-11 min-h-11 items-center justify-between border-b border-[#d1d5db] bg-[#f2f7fc] pl-3.5 pr-4'>
              <DialogTitle className='flex items-center text-[16px] font-bold text-black'>
                <AritoIcon icon={260} className='mx-2' />
                <span>{title}</span>
              </DialogTitle>
              <button type='button' onClick={handleClose} className='inline-flex size-8 items-center justify-center'>
                <Lineicons aria-hidden='true' icon={XmarkStroke} className='size-[18px]' />
              </button>
            </div>
            <div className='min-w-[700px] p-4'>
              <div className='mb-4 flex items-center gap-6'>
                <Label className='min-w-[60px] text-sm font-medium'>Xử lý</Label>
                <label className='flex items-center'>
                  <input
                    type='radio'
                    name='action'
                    value='cancel'
                    checked={selectedAction === 'cancel'}
                    onChange={event => setSelectedAction(event.target.value as 'cancel')}
                    className='mr-2'
                  />
                  <span className='text-sm'>Hủy (Hủy bỏ đơn từ)</span>
                </label>
                <label className='flex items-center'>
                  <input
                    type='radio'
                    name='action'
                    value='reject'
                    checked={selectedAction === 'reject'}
                    onChange={event => setSelectedAction(event.target.value as 'reject')}
                    className='mr-2'
                  />
                  <span className='text-sm'>Từ chối (không đồng ý, xem lại lý do và thực hiện)</span>
                </label>
              </div>
              <div className='mb-4 flex items-start'>
                <Label className='mt-2 min-w-[60px] text-sm font-medium'>Lý do</Label>
                <div className='ml-6 flex-1'>
                  <textarea
                    value={reason}
                    onChange={event => setReason(event.target.value)}
                    placeholder='Lý do hủy/từ chối phê duyệt đơn từ'
                    className='min-h-[80px] w-full resize-none rounded border border-gray-300 p-2 text-sm focus:border-blue-500 focus:outline-none'
                    rows={3}
                  />
                </div>
              </div>
            </div>
            <div className='border-gray-150 border-t bg-[#f2f7fc]'>
              <BottomActionBarV2 mode='search' onClose={handleClose} onSubmit={handleConfirm} submitTitle='Đồng ý' />
            </div>
          </DialogPrimitive.Content>
        </div>
      </DialogPortal>
    </Dialog>
  );
};

export default RejectDialog;
